import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrganizationScoping } from './useOrganizationScoping';
import { toast } from '@/hooks/use-toast';

export interface OrganizationAnalytics {
  totalBookings: number;
  totalBookingsChange: number;
  activeMembers: number;
  activeMembersChange: number;
  completedBookings: number;
  completedBookingsChange: number;
  revenueGenerated: number;
  revenueChange: number;
  bookingsByStatus: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
  bookingsTrend: Array<{
    date: string;
    count: number;
  }>;
  topUsers: Array<{
    name: string;
    email: string;
    bookings: number;
  }>;
}

export const useOrganizationAnalytics = (explicitOrgId?: string) => {
  const [analytics, setAnalytics] = useState<OrganizationAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const { organizationId: scopedOrgId } = useOrganizationScoping();
  
  // Use explicit org ID if provided, otherwise fall back to scoped org ID
  const organizationId = explicitOrgId || scopedOrgId;

  useEffect(() => {
    if (organizationId) {
      fetchAnalytics();
    }
  }, [organizationId]);

  const fetchAnalytics = async () => {
    if (!organizationId) return;
    
    try {
      setLoading(true);
      
      // Get date ranges
      const now = new Date();
      const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
      
      // Fetch bookings for this month
      const { data: thisMonthBookings, error: thisMonthError } = await supabase
        .from('bookings')
        .select('*')
        .eq('org_id', organizationId)
        .gte('created_at', thisMonth.toISOString());

      if (thisMonthError) throw thisMonthError;

      // Fetch bookings for last month
      const { data: lastMonthBookings, error: lastMonthError } = await supabase
        .from('bookings')
        .select('*')
        .eq('org_id', organizationId)
        .gte('created_at', lastMonth.toISOString())
        .lte('created_at', lastMonthEnd.toISOString());

      if (lastMonthError) throw lastMonthError;

      // Fetch organization members
      const { data: members, error: membersError } = await supabase
        .from('organization_user_roles')
        .select('*')
        .eq('org_id', organizationId);

      if (membersError) throw membersError;

      // Fetch quotes for revenue calculation
      const { data: quotes, error: quotesError } = await supabase
        .from('quotes')
        .select('quoted_amount, payment_confirmed')
        .eq('org_id', organizationId);

      if (quotesError) throw quotesError;

      // Calculate analytics
      const totalBookings = thisMonthBookings?.length || 0;
      const lastMonthTotal = lastMonthBookings?.length || 0;
      const totalBookingsChange = lastMonthTotal > 0 
        ? ((totalBookings - lastMonthTotal) / lastMonthTotal) * 100 
        : 0;

      const activeMembers = members?.length || 0;
      const activeMembersChange = 0; // Would need historical data

      const completedBookings = thisMonthBookings?.filter(
        b => ['delivered', 'completed'].includes(b.status)
      ).length || 0;
      const lastMonthCompleted = lastMonthBookings?.filter(
        b => ['delivered', 'completed'].includes(b.status)
      ).length || 0;
      const completedBookingsChange = lastMonthCompleted > 0
        ? ((completedBookings - lastMonthCompleted) / lastMonthCompleted) * 100
        : 0;

      const revenueGenerated = quotes?.filter(q => q.payment_confirmed)
        .reduce((sum, q) => sum + (q.quoted_amount || 0), 0) || 0;
      const revenueChange = 0; // Would need historical data

      // Group bookings by status
      const statusCounts = thisMonthBookings?.reduce((acc, booking) => {
        acc[booking.status] = (acc[booking.status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      const bookingsByStatus = Object.entries(statusCounts).map(([status, count]) => ({
        status,
        count,
        percentage: (count / totalBookings) * 100
      }));

      // Get daily bookings trend for the last 30 days
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data: trendData, error: trendError } = await supabase
        .from('bookings')
        .select('created_at')
        .eq('org_id', organizationId)
        .gte('created_at', thirtyDaysAgo.toISOString());

      if (trendError) throw trendError;

      const bookingsTrend = Array.from({ length: 30 }, (_, i) => {
        const date = new Date(thirtyDaysAgo);
        date.setDate(date.getDate() + i);
        const dateStr = date.toISOString().split('T')[0];
        const count = trendData?.filter(b => 
          b.created_at.startsWith(dateStr)
        ).length || 0;
        return { date: dateStr, count };
      });

      // Get top users by booking count
      const { data: topUsersData, error: topUsersError } = await supabase
        .from('bookings')
        .select(`
          user_id,
          profiles!bookings_user_id_fkey (
            name,
            email
          )
        `)
        .eq('org_id', organizationId);

      if (topUsersError) throw topUsersError;

      const userBookingCounts = topUsersData?.reduce((acc, booking) => {
        const userId = booking.user_id;
        if (!acc[userId] && booking.profiles) {
          acc[userId] = {
            name: booking.profiles.name || 'Unknown',
            email: booking.profiles.email || 'Unknown',
            bookings: 0
          };
        }
        if (acc[userId]) {
          acc[userId].bookings++;
        }
        return acc;
      }, {} as Record<string, { name: string; email: string; bookings: number }>) || {};

      const topUsers = Object.values(userBookingCounts)
        .sort((a, b) => b.bookings - a.bookings)
        .slice(0, 10);

      setAnalytics({
        totalBookings,
        totalBookingsChange,
        activeMembers,
        activeMembersChange,
        completedBookings,
        completedBookingsChange,
        revenueGenerated,
        revenueChange,
        bookingsByStatus,
        bookingsTrend,
        topUsers,
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast({
        title: "Error",
        description: "Failed to load analytics data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    analytics,
    loading,
    refetch: fetchAnalytics,
  };
};